/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Oct 4 2023
 **/

import { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { useTheme } from "@emotion/react";
import { useQuery } from "@tanstack/react-query";

import {
	Button,
	Column,
	Divider,
	Menu,
	Row,
	Select,
	TextField,
	Tooltip,
	Typography,
	useModal,
	useToast
} from "@/frameworks/designStuff/components";
import { Utility } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";
import { usePersistActions } from "@/frameworks/core/persistence";
import { useUnityCommand } from "@/frameworks/core/unity";
import { useUsers } from "@/frameworks/core/API";
import { ModalID, POPOVER_ID, RegExp, SPACE_SCENE_TYPE, ZIndex } from "@/frameworks/core/commons";
import { usePopover } from "@/frameworks/core/hooks";
import * as S from "../../emotion";
import { editButtonStyle } from "../../emotion";
import Layout from "../../Layout";
import { ProfileColor } from "../../atom";
import ProfileEditor from "./ProfileEditor";
import ChangePassword from "./ChangePassword";
import { prioritySortSpace } from "./utils";
import ProfileImagesPopover from "./ProfileImagesPopover";

export default function MyInfo({ spaceData }: { spaceData: SwaggerModels.MySpaceListResponseVO | undefined }) {
	const [selectedColorName, setSelectedColorName] = useAtom(ProfileColor);
	const inputRef = useRef<HTMLInputElement>(null);
	const [editImage, setEditImage] = useState<File | null>(null);
	const [isRemoveProfileImage, setIsRemoveProfileImage] = useState(false);
	const modal = useModal();
	const theme = useTheme();
	const popover = usePopover();
	const unityCommand = useUnityCommand();
	const [profileImageId, setProfileImageId] = useState("");
	const { users } = usePersistActions();
	const userAPI = useUsers();

	useEffect(() => {
		if (users) {
			setUserData(users.me);
			setSelectedColorName(`${users.me.colorProfile}_50_100` as Avatar.ProfileColor);
			const entrySpaceID = users.me?.entrySpace?.spaceId || users.me?.myRoomId || null;
			const entrySpaceType = users.me?.entrySpace?.spaceType || SPACE_SCENE_TYPE.MY_ROOM;
			setEntrySpace({
				id: entrySpaceID || "",
				type: entrySpaceType
			});
		}
	}, [users]);

	const toast = useToast();

	const [userData, setUserData] = useState<Partial<WebServices.User.Me.Inquiry.Data>>({});
	const [pwd, setPwd] = useState({
		pwd: "",
		pwdConfirm: ""
	});
	const [activePwd, setActivePwd] = useState(false);
	const [entrySpace, setEntrySpace] = useState<{ id: string | null; type: string | null }>({
		id: null,
		type: null
	});

	const { data: myData } = useQuery({
		queryKey: ["userInfo"],
		queryFn: async () => (await userAPI.me.inquiry.execute())?.data,
		refetchOnMount: true
	});

	const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const target = e.target as HTMLInputElement;
		if (!target.files) {
			toast.show({ type: "error", message: _T("파일이 없습니다.") });
			return;
		}
		if (target.files[0].size > 10000000) {
			toast.show({ type: "error", message: _T("프로필 이미지 등록 용량을 초과하였습니다") });
		} else {
			if (target.files[0]?.type === "image/gif") {
				setEditImage(target.files[0]);
				const base64 = await processFile(target.files[0]);
				setUserData(
					(prev) =>
						({
							...prev,
							profileImagePath: base64
						}) as WebServices.User.Me.Inquiry.Data
				);
				setIsRemoveProfileImage(false);
			} else {
				modal.show({
					id: ModalID.PROFILE_EDITOR,
					content: (
						<ProfileEditor
							src={target.files[0]}
							handleImage={handleEditImage}
							onHide={() => {
								modal.hide(ModalID.PROFILE_EDITOR);
							}}
						/>
					)
				});
			}
		}
		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};
	const handleEditImage = async (file: Blob) => {
		setEditImage(file as File);
		const base64 = await processFile(file);
		setUserData(
			(prev) =>
				({
					...prev,
					profileImagePath: base64
				}) as WebServices.User.Me.Inquiry.Data
		);
		setIsRemoveProfileImage(false);
	};

	const processFile = (file: Blob) => {
		return new Promise<string>((resolve) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onloadend = () => {
				resolve(reader.result as string);
			};
		});
	};

	const handleProfileRemove = async () => {
		const { data } = await userAPI.avatar.current.execute();
		setUserData(
			(prev) =>
				({
					...prev,
					//@ts-ignore
					profileImagePath: data?.faceImagePath
				}) as WebServices.User.Me.Inquiry.Data
		);

		if (inputRef.current) {
			inputRef.current.value = "";
		}

		setEditImage(null);
		setIsRemoveProfileImage(true);
		popover.hide(POPOVER_ID.USER_PROFILE_EDIT);
	};

	const handleSave = async () => {
		try {
			const payload = {
				name: userData?.name?.trim() ?? "",
				phone: userData?.phoneNumber,
				shortDescription: userData?.shortDescription,
				description: userData?.description,
				marketingAgreement: false,
				entrySpaceId: entrySpace.id,
				entrySpaceType: entrySpace.type,
				colorProfile: selectedColorName.split("_")[0],
				removeProfileImage: isRemoveProfileImage
			};
			const payloadImageFile = {
				customProfileImage: editImage
					? editImage
					: (inputRef.current?.files && inputRef.current?.files[0]) || null,
				defaultProfileImageId: undefined,
				...payload
			} as WebServices.User.Me.Update.Request;

			const payloadProfileImage = {
				defaultProfileImageId: profileImageId || undefined,
				...payload
			} as WebServices.User.Me.Update.Request;

			await users.update_me(editImage ? payloadImageFile : payloadProfileImage);
			const response = await users.refresh_user(userData?.userId || "");
			if (response) {
				try {
					// 유니티로 업데이트된 사용자 정보 전달
					unityCommand.updateUserProfile({
						name: response?.name || "",
						profileImagePath: response?.profileImagePath || "",
						userID: response?.userId || ""
					});
				} catch (error: unknown) {
					console.warn("#MyInfo# notify user profile failed", error);
				}
				toast.show({ type: "success", message: _T("저장되었습니다.") });
				modal.hide(ModalID.SETTINGS);
			}
			setIsRemoveProfileImage(false);
		} catch (error) {
			toast.show({ type: "error", message: _T("문제가 발생하였습니다") });
		}
	};
	const isValidPassword = (pwd: string) => {
		return RegExp.PASSWORD_REG.test(pwd);
	};

	const handlePwdChange = async () => {
		if (!isValidPassword(pwd.pwd)) {
			return toast.show({
				type: "error",
				message: _T("8자 이상 24자 이하 영문, 숫자, 특수문자를 혼합하여 사용해 주세요.")
			});
		}

		if (activePwd && pwd.pwd !== pwd.pwdConfirm) {
			return toast.show({ type: "error", message: _T("비밀번호가 일치하지 않습니다.") });
		}

		await userAPI.me.update.execute({
			name: userData?.name?.trim(),
			password: pwd.pwd,
			marketingAgreement: false
		});
	};

	const handleActivePwdChange = (active: boolean) => {
		setActivePwd(active);
	};

	const handleSpace = (id: string, type: WebServices.Commons.CurrentSpaceType) => {
		setEntrySpace({
			id,
			type
		});
	};

	useEffect(() => {
		if (editImage) {
			setUserData(
				(prev) =>
					({
						...prev,
						profileImagePath: URL.createObjectURL(editImage)
					}) as WebServices.User.Me.Inquiry.Data
			);
		}
	}, [editImage]);

	useEffect(() => {
		return () => {
			popover.getPopoverState(POPOVER_ID.USER_PROFILE_EDIT).isOpen && popover.hide(POPOVER_ID.USER_PROFILE_EDIT);
		};
	}, []);

	const showProfileImages = (ev: React.MouseEvent) => {
		popover.show({
			popoverID: POPOVER_ID.PROFILE_IMAGES,
			content: (
				<ProfileImagesPopover
					currentProfileImage={userData.profileImagePath ?? ""}
					onSelectProfileImg={(profileImg: { id: string; path?: string }) => {
						setProfileImageId(profileImg.id);
						setUserData(
							(prev) =>
								({
									...prev,
									//@ts-ignore
									defaultProfileImageId: profileImg.id,
									profileImagePath: profileImg.path
								}) as WebServices.User.Me.Inquiry.Data
						);
						setIsRemoveProfileImage(false);
						popover.hide(POPOVER_ID.PROFILE_IMAGES);
					}}
				/>
			),
			anchorEl: ev.currentTarget,
			direction: {
				x: "left",
				y: "top"
			},
			position: {
				x: 160,
				y: 0
			}
		});
	};

	const handleProfile = (e: React.MouseEvent<HTMLElement>) => {
		popover.show({
			popoverID: POPOVER_ID.USER_PROFILE_EDIT,
			content: (
				<Menu css={{ background: theme.colors.neutral_00_100 }}>
					<Menu.List>
						<Menu.Item
							onClick={() => {
								inputRef.current?.click();
								popover.hide(POPOVER_ID.USER_PROFILE_EDIT);
							}}
						>
							{_T("앨범에서 선택")}
						</Menu.Item>
						<Menu.Item onClick={(e) => showProfileImages(e)}>{_T("기본 이미지에서 선택")}</Menu.Item>
					</Menu.List>
					<Menu.List>
						<Maybe test={Boolean(myData?.profileImagePath)}>
							{!(myData?.profileImagePath === myData?.avatarProfileImagePath || isRemoveProfileImage) && (
								<>
									<Divider type="line" />
									<Menu.Item
										onClick={handleProfileRemove}
										typoColor="warning_80_100"
										//@ts-ignore
										disabled={
											myData?.profileImagePath === myData?.avatarProfileImagePath ||
											isRemoveProfileImage
										}
									>
										{_T("사진 제거")}
									</Menu.Item>
								</>
							)}
						</Maybe>
					</Menu.List>
				</Menu>
			),
			anchorEl: e.currentTarget,
			direction: {
				x: "right",
				y: "bottom"
			},
			zIndex: ZIndex.POPUP + 1
		});
	};

	const checkSpecialCharacters = (str?: string) => {
		const regex = /[<>]/;
		if (str) {
			return regex.test(str);
		}
	};

	const handleShortDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
		setUserData(
			(prev) =>
				({
					...prev,
					shortDescription: e.target.value
				}) as WebServices.User.Me.Inquiry.Data
		);
	};

	return (
		<Layout
			title="My"
			handleSave={handleSave}
			disabled={!entrySpace.id || !userData.name || checkSpecialCharacters(userData?.name)}
		>
			<S.ContentArea>
				<S.AvatarArea gap={4}>
					<Typography.p typoSize="Body4" typoColor="neutral_60_100">
						{_T("프로필 이미지 (필수)")}
					</Typography.p>
					<S.AvatarEditArea>
						<S.AvatarWrapper>
							<S.Avatar
								type="image"
								kind="people_64"
								src={userData?.profileImagePath}
								text={(userData?.name || "").substring(0, 1)}
								textColor={`${selectedColorName}`}
								onClick={() => {
									if (myData?.profileImagePath && myData?.profileImagePath.length > 0) {
										modal.show({
											id: ModalID.PROFILE_EDITOR,
											content: (
												<ProfileEditor
													src={`${userData?.profileImagePath}`}
													handleImage={handleEditImage}
													onHide={() => modal.hide(ModalID.PROFILE_EDITOR)}
												/>
											)
										});
									}
								}}
							/>
							<Button.Icon
								name="Edit24"
								size="medium"
								css={editButtonStyle(theme)}
								onClick={handleProfile}
							/>
							<input
								type="file"
								style={{ display: "none" }}
								onChange={handleChange}
								accept="image/png, image/jpeg, image/jpg, image/gif"
								ref={inputRef}
							/>
						</S.AvatarWrapper>
					</S.AvatarEditArea>
				</S.AvatarArea>
				<Column gap={15}>
					<Row verticalAlign="bottom" gap={12}>
						<Row>
							<TextField
								label={_T("계정")}
								css={{ width: "262px" }}
								readOnly
								defaultValue={users.me?.username}
							/>
						</Row>
						<Row>
							<Maybe
								test={activePwd}
								fallback={
									<Button
										filled="secondary"
										css={{ minWidth: "160px" }}
										size="Xlarge"
										onClick={() =>
											modal.show({
												id: ModalID.RESET_PASSWORD,
												content: <ChangePassword />,
												preventHideByDimmed: true
											})
										}
									>
										{_T("비밀번호 변경")}
									</Button>
								}
							>
								<Row gap={5}>
									<Button
										css={{ minWidth: "65px" }}
										onClick={handlePwdChange}
										size="Xlarge"
										disabled={pwd.pwd.length === 0}
									>
										{_T("변경")}
									</Button>
									<Button
										css={{ width: "65px" }}
										size="Xlarge"
										onClick={() => handleActivePwdChange(false)}
									>
										{_T("취소")}
									</Button>
								</Row>
							</Maybe>
						</Row>
					</Row>

					<Maybe test={activePwd}>
						<Row gap={10}>
							<TextField
								type="password"
								label={_T("새 비밀번호 입력")}
								help={_T("8자~24자. 영문, 숫자, 특수문자 포함")}
								value={pwd.pwd}
								maxLength={50}
								onChange={(e) => {
									setPwd((prev) => ({
										...prev,
										pwd: e.target.value
									}));
								}}
							/>

							<TextField
								type="password"
								label={_T("새 비밀번호 확인")}
								value={pwd.pwdConfirm}
								maxLength={50}
								onChange={(e) => {
									setPwd((prev) => ({
										...prev,
										pwdConfirm: e.target.value
									}));
								}}
							/>
						</Row>
					</Maybe>
					<Row gap={12}>
						<TextField
							label={_T("이름(필수)")}
							error={!userData?.name || checkSpecialCharacters(userData?.name)}
							help={
								userData?.name
									? checkSpecialCharacters(userData?.name)
										? _T("사용 할 수 없는 특수문자가 포함되어 있습니다.")
										: ""
									: _T("이름을 입력해주세요.")
							}
							value={String(userData?.name || "")}
							maxLength={50}
							onChange={(e) => {
								setUserData(
									(prev) =>
										({
											...prev,
											name: Utility.sanitizeInput(e.target.value)
										}) as WebServices.User.Me.Inquiry.Data
								);
							}}
						/>
						<TextField
							maxLength={25}
							label={_T("휴대전화번호")}
							value={userData?.phoneNumber || ""}
							onChange={(e) => {
								setUserData(
									(prev) =>
										({
											...prev,
											phoneNumber: e.target.value
										}) as WebServices.User.Me.Inquiry.Data
								);
							}}
						/>
					</Row>
					<Column css={{ width: "100%" }}>
						<TextField
							maxLength={25}
							label={_T("한줄 소개")}
							defaultValue={userData?.shortDescription}
							css={{ width: "100%" }}
							onInput={handleShortDescription}
						/>
						<Typography.p typoSize="Body5" typoColor="neutral_60_100" css={{ paddingLeft: "12px" }}>
							{_T("한줄소개는 프로필에 노출되며, 미 입력 시 기본 한줄소개가 랜덤으로 노출됩니다")}
						</Typography.p>
					</Column>
					<Row css={{ width: "100%" }}>
						<TextField
							label={_T("소개")}
							kind="textarea"
							value={userData?.description || ""}
							maxLength={100}
							css={{ width: "100%", height: "97px", padding: "10px" }}
							onChange={(e) => {
								setUserData(
									(prev) =>
										({
											...prev,
											description: e.target.value
										}) as WebServices.User.Me.Inquiry.Data
								);
							}}
						/>
					</Row>
					{/* TODO 오픈월드 영역은 어드민 상태에따라 분기처리 해야함 */}
					<Row gap={10}>
						<Select
							error={!entrySpace.id}
							help={entrySpace.id ? "" : _T("기본 접속 스페이스를 설정해주세요.")}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								const { meta: type } = e.target.dataset;
								const spaceID = e.target.value;

								switch (type) {
									case "MY_ROOM": {
										handleSpace(spaceID, type);
										break;
									}
									case "SPACE": {
										handleSpace(spaceID, type);
										break;
									}
								}
							}}
							value={
								users.me?.entrySpace?.spaceType === "MY_ROOM" || !users.me?.entrySpace
									? _T("마이룸")
									: users.me?.entrySpace?.spaceName || "-"
							}
							label={_T("기본 접속 스페이스")}
							labelIcon={
								<Tooltip
									message={_T("로그인 시 설정된 스페이스에서 시작합니다")}
									position="top"
									hasIcon={false}
								>
									<Button.Icon name="Help24" size="small_18" />
								</Tooltip>
							}
							options={[
								{
									label: _T("마이룸") || "",
									value: String(spaceData?.myRoom?.id || ""),
									meta: "MY_ROOM"
								},
								{
									label: _T("오픈월드"),
									value: "",
									isSubLabel: true
								},
								{
									label: spaceData?.openWorlds?.[0]?.name || "",
									value: spaceData?.openWorlds?.[0]?.spaceId || "",
									meta: "SPACE"
								},
								{
									label: _T("알서포트"),
									value: "",
									isSubLabel: true
								},
								...prioritySortSpace(spaceData?.groups ?? [])
							]}
							css={{ maxWidth: 262 }}
						/>
					</Row>
				</Column>
			</S.ContentArea>
		</Layout>
	);
}
